import { ReactNode } from "react";

import {
  AnalysisIcon,
  MakerIcon,
  MapIcon,
  VerticalBarIcon
} from "@/assets/icons";

import type { LokasiAIProviderProps } from "@lokasi-ai-web-sdk/app";

/**
 * The SDK does not export `ConversationTopic` from its public barrel, so the
 * topic list type is derived from the provider props instead.
 */
type ConversationTopics = NonNullable<LokasiAIProviderProps["topics"]>;

export type SuggestedPrompt = {
  id: string;
  title: string;
  description: string;
  icon: ReactNode;
  /** Message sent verbatim to the assistant when the card is picked. */
  prompt: string;
};

/**
 * Topics attached to every Agentic AI prompt.
 *
 * `ConversationTopic` is a closed union in `@lokasi-ai-web-sdk/app`
 * (`"spatial_analysis" | "dataset_recommendation"`), so there is no dedicated
 * topic for a free-form conversation yet. Left empty until the backend defines
 * one — swap this single constant once it exists.
 */
export const AGENTIC_AI_TOPICS: ConversationTopics = [];

/**
 * One entry in the chat history sidebar.
 *
 * `timeLabel` is pre-formatted by the container so the list stays
 * presentational — it never touches a clock.
 */
export type ChatHistoryEntry = {
  id: string;
  title: string;
  timeLabel: string;
};

export const AGENTIC_AI_SIDEBAR_TITLE = "AI Intelligence";

export const AGENTIC_AI_CHAT_HISTORY_HEADING = "Chats";

export const AGENTIC_AI_CHAT_HISTORY_EMPTY =
  "No chats yet. Start a conversation and it will show up here.";

export const AGENTIC_AI_HEADING = "What would you like to explore today?";

export const AGENTIC_AI_DESCRIPTION =
  "Ask anything about locations, markets, competitors, datasets, and business opportunities.";

export const AGENTIC_AI_INPUT_PLACEHOLDER =
  "Ask Agentic AI anything about locations...";

export const AGENTIC_AI_FOLLOW_UP_PLACEHOLDER = "Ask a follow-up question...";

export const AGENTIC_AI_THINKING_LABEL = "Thinking…";

export const AGENTIC_AI_SUGGESTED_PROMPTS: SuggestedPrompt[] = [
  {
    id: "locations",
    title: "Locations",
    description: "Find potential areas for a new store or expansion.",
    icon: <MakerIcon width={20} height={20} />,
    prompt: "Find potential areas for a new store or expansion."
  },
  {
    id: "competitors",
    title: "Competitors",
    description: "Analyze competitor presence and performance in an area.",
    icon: <AnalysisIcon viewBox="5 4.29 14 14" />,
    prompt: "Analyze competitor presence and performance in an area."
  },
  {
    id: "market",
    title: "Market",
    description: "Understand market potential and demand drivers.",
    icon: <VerticalBarIcon />,
    prompt: "Understand market potential and demand drivers."
  },
  {
    id: "map-exploration",
    title: "Map exploration",
    description: "Explore data and insights visually on the map.",
    icon: <MapIcon />,
    prompt: "Explore data and insights visually on the map."
  }
];
